import { VENUES, type GameId, type LobbyResponse } from '@tira/protocol';
import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';
import { SCENE_H } from './parts/GameRoom.js';
import { ChipCount } from '../components/ChipCount.js';
import { FullscreenButton } from '../components/FullscreenButton.js';
import { Icon } from '../components/Icon.js';
import { Wordmark } from '../components/Wordmark.js';
import { useT, type T } from '../i18n/index.js';
import { api } from '../net/api.js';
import { useApp } from '../state/store.js';
import { StationArt } from './parts/StationArt.js';
import './Compound.css';

/**
 * S3 — the compound at night. Six stations along one strip; you glide the
 * camera through it and walk up to a table. Selecting a station is arriving at
 * a place, not opening a menu (design spec §4).
 *
 * Walk order is spatial, not alphabetical: you come in off the street, so the
 * bangketa is first, then the store bench, the garahe, the carport, up to the
 * balcony, and the outpost last, down the road.
 */
const WALK: GameId[] = ['dama', 'tongits', 'pusoydos', 'mahjong', 'pusoy', 'gera'];

const LOBBY_POLL_MS = 25_000;

type StationState = 'live' | 'soon' | 'offline' | 'loading';

export function Compound(): JSX.Element {
  const go = useApp((s) => s.go);
  const balance = useApp((s) => s.balance);
  const t = useT();
  const [lobby, setLobby] = useState<LobbyResponse | null>(null);
  const [lobbyDown, setLobbyDown] = useState(false);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const fitRef = useRef<HTMLDivElement | null>(null);
  const fit = useHeightFit(fitRef);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .lobby()
        .then((r) => {
          if (!alive) return;
          setLobby(r);
          setLobbyDown(false);
        })
        .catch(() => {
          if (alive) setLobbyDown(true);
        });
    };
    load();
    const t = setInterval(load, LOBBY_POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  /**
 * The compound is scaled to the window's height. Unlike the room it does not
 * fit the width too: it is a strip, and a wider window should show more of it
 * rather than the same picture with bars down the side.
 */
function useHeightFit(ref: React.RefObject<HTMLElement | null>): number {
  const [fit, setFit] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setFit(h / SCENE_H);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return fit;
}

/** The camera: scroll position drives the parallax bands, nothing else. */
  const onScroll = useCallback(() => {
    const strip = stripRef.current;
    const root = rootRef.current;
    if (strip && root) root.style.setProperty('--cam', String(strip.scrollLeft));
  }, []);

  const glide = (dir: -1 | 1) => {
    const strip = stripRef.current;
    if (!strip) return;
    const step = strip.clientWidth * 0.8;
    strip.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className="compound" ref={fitRef}>
      <div className="compound__fit" ref={rootRef} style={{ '--fit': fit } as CSSProperties}>
      <div className="compound__band compound__band--sky" />
      <div className="compound__band compound__band--ridge" />
      <div className="compound__band compound__band--wall" />
      <div className="compound__band compound__band--ground" />

      <header className="compound__rail">
        <Wordmark size={22} />
        <p className="compound__rail-strap">{t('lobby.rail.strap')}</p>
        <div className="s-rail__spacer" />
        <button className="s-rail__wallet" onClick={() => go({ s: 'wallet' })}>
          <ChipCount value={balance} compact />
        </button>
        <FullscreenButton />
        <button
          className="s-iconbtn"
          onClick={() => go({ s: 'store' })}
          title={t('lobby.rail.store')}
        >
          <Icon name="tindahan" size={22} label={t('lobby.rail.store')} />
        </button>
        <button
          className="s-iconbtn"
          onClick={() => go({ s: 'wallet' })}
          title={t('lobby.rail.wallet')}
        >
          <Icon name="chip" size={22} label={t('lobby.rail.wallet')} />
        </button>
        <button className="s-iconbtn" onClick={() => go({ s: 'kubo' })} title={t('lobby.rail.kubo')}>
          <Icon name="kubo" size={22} label={t('lobby.rail.kubo.label')} />
        </button>
        <button
          className="s-iconbtn"
          onClick={() => go({ s: 'settings' })}
          title={t('lobby.rail.settings')}
        >
          <Icon name="responsible" size={22} label={t('lobby.rail.settings.label')} />
        </button>
      </header>

      <div className="compound__strip" ref={stripRef} onScroll={onScroll}>
        <div className="compound__track">
          {WALK.map((game) => (
            <Station
              key={game}
              game={game}
              lobby={lobby}
              lobbyDown={lobbyDown}
              onEnter={() => go({ s: 'venue', game })}
            />
          ))}
        </div>
      </div>

      <p className="compound__hint">{t('lobby.hint.swipe')}</p>
      <nav className="compound__nav">
        <button onClick={() => glide(-1)} aria-label={t('lobby.nav.prev')}>
          ‹
        </button>
        <button onClick={() => glide(1)} aria-label={t('lobby.nav.next')}>
          ›
        </button>
      </nav>
      </div>
    </div>
  );
}

interface StationProps {
  game: GameId;
  lobby: LobbyResponse | null;
  lobbyDown: boolean;
  onEnter: () => void;
}

function Station({ game, lobby, lobbyDown, onEnter }: StationProps): JSX.Element {
  const venue = VENUES[game];
  const t = useT();
  const live = lobby?.venues.find((v) => v.game === game) ?? null;

  /**
   * The server decides what is open: `available` is derived from the engine
   * registry, so a venue opens the moment its rules ship. The client never
   * guesses a table into existence, and — the lesson here — never guesses one
   * out of existence either. Gating on the wave number meant Mahjong stayed
   * "Malapit na" on a server that was happily starting Mahjong matches.
   *
   * The wave is only a hint for the moment before the first lobby response
   * lands, so a venue that certainly has not shipped does not flash as
   * available. Walking in on an unanswered lobby is not free: `queue.join`
   * escrows the buy-in, and a venue with no engine fails only once the table
   * fills, with the chips already held.
   */
  const state: StationState = lobbyDown
    ? 'offline'
    : lobby === null
      ? venue.wave > 1
        ? 'soon'
        : 'loading'
      : live?.available
        ? 'live'
        : 'soon';

  const openTables = live ? live.tables.reduce((n, t) => n + t.open, 0) : 0;
  // What a table here is dealt, which is `seats[0]` everywhere on the server.
  // Pusoy declares [2,3,4] and has only ever been built heads-up, so listing all
  // three advertised a game the compound cannot open.
  const seats = String(venue.seats[0] ?? 2);

  return (
    <button
      className="station"
      data-state={state}
      style={{ '--accent': `var(--venue-${game})` } as CSSProperties}
      disabled={state !== 'live'}
      onClick={onEnter}
      aria-label={t('lobby.station.aria', {
        game: t(`venue.${game}`),
        station: t(`venue.${game}.station`),
      })}
    >
      <div className="station__frame">
        <StationArt game={game} />
        <div className="station__glow" />
      </div>

      <div className="station__plate">
        <div className="station__sign">
          <b>{t(`venue.${game}`)}</b>
          <span>{t(`venue.${game}.station`)}</span>
        </div>
        <p className="station__tag">{t(`lobby.venue.${game}.tag`)}</p>

        <div className="station__meta">
          {state === 'live' ? (
            <>
              <span className="station__count">
                <i />
                {live
                  ? t('lobby.station.playing', { n: live.playersOnline })
                  : t('lobby.station.checking')}
              </span>
              <span className="s-tag">{t('lobby.station.seats', { n: seats })}</span>
              {/*
                This counts open TABLES across the three stake tiers, not empty
                seats. Labelling it "bakante" next to "2 upuan" read as "2 seats,
                4 vacant" — impossible at a two-seat game, and the first thing a
                player would notice.
              */}
              {live && openTables > 0 ? (
                <span className="s-tag s-tag--live">
                  {t('lobby.station.open', { n: openTables })}
                </span>
              ) : null}
            </>
          ) : state === 'soon' ? (
            <span className="s-tag s-tag--soon">{t('lobby.station.soon')}</span>
          ) : state === 'loading' ? (
            <span className="s-tag">{t('lobby.station.checking')}</span>
          ) : (
            <span className="s-tag s-tag--warn">{t('lobby.station.offline')}</span>
          )}
        </div>

        <span className="station__cta">{ctaFor(state, live?.playersOnline ?? 0, t)}</span>
      </div>
    </button>
  );
}

function ctaFor(state: StationState, online: number, t: T): string {
  if (state === 'soon') return t('lobby.cta.soon');
  if (state === 'offline') return t('lobby.cta.offline');
  if (state === 'loading') return t('lobby.cta.loading');
  return online === 0 ? t('lobby.cta.empty') : t('lobby.cta.enter');
}
